import React from 'react';
import useResponsive from '@/hooks/useResponsive';
import type { SubNavProps } from '../types';

export interface UseSubNavArgs {
  variant?: SubNavProps['variant'];
  side?: SubNavProps['side'];
  isOpen?: boolean;
  onClose?: () => void;
}

export interface UseSubNavResult {
  currentVariant: Exclude<SubNavProps['variant'], 'auto'>;
  currentSide?: SubNavProps['side'];
  openForSheet?: boolean;
  isMobile: boolean;
  isTablet: boolean;
}

export function useSubNav({
  variant = 'auto',
  side = 'left',
  isOpen = true,
  onClose,
}: UseSubNavArgs): UseSubNavResult {
  const { isMobile, isTablet } = useResponsive();

  const currentVariant = React.useMemo<Exclude<SubNavProps['variant'], 'auto'>>(() => {
    if (variant !== 'auto') return variant as Exclude<SubNavProps['variant'], 'auto'>;
    return isMobile ? 'accordion' : (isTablet ? 'sheet' : 'card');
  }, [variant, isMobile, isTablet]);

  const currentSide = React.useMemo<SubNavProps['side'] | undefined>(() => {
    if (currentVariant !== 'sheet') return undefined;
    return side ?? (isMobile ? 'bottom' : 'left');
  }, [currentVariant, side, isMobile]);

  // Centralized ESC handling for card variant
  React.useEffect(() => {
    if (currentVariant !== 'card' || !onClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentVariant, onClose]);

  const openForSheet = currentVariant === 'sheet' ? isOpen : undefined;

  return { currentVariant, currentSide, openForSheet, isMobile, isTablet };
}
